import React, { useState } from 'react'
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../app/hooks';
import { getAuthUserInfo } from '../../features/userLogin/userLoginSlice';
import { UserSidebar } from './UserSidebar'
import { AvoRedApp } from '../../components/Layout/AvoRedApp';
import { useQuery } from 'urql';
import { ALL_ORDERS_QUERY, ORDER_ITEMS_QUERY } from '../../graphql/queries/orderQuery';

interface Product {
  id: string;
  name: string;
  slug: string;
  main_image_url?: string | null;
}

interface ProductVariant {
  id: string;
  name: string;
  sku?: string | null;
}

interface Branch {
  id: string;
  name: string;
  address?: string | null;
}

interface OrderItem {
  id: string;
  product_id?: string | null;
  variant_id?: string | null;
  quantity?: number | null;
  qty?: number | null;
  price: number;
  subtotal?: number | null;
  product?: Product | null;
  productVariant?: ProductVariant | null;
}

interface Order {
  id: string;
  track_code: string;
  status: string;
  total: number;
  branch?: Branch | null;
  created_at: string;
  updated_at?: string | null;
}

interface PaginationInfo {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  has_more_pages: boolean;
}

interface OrderItemsResponse {
  data: OrderItem[];
  pagination: PaginationInfo;
}

interface OrdersResponse {
  data: Order[];
  pagination: PaginationInfo;
}

interface OrderDetailsModalProps {
  order: Order;
  isOpen: boolean;
  onClose: () => void;
}

// Helper functions for status display
const getStatusBadgeClass = (status: string): string => {
  const statusClasses: Record<string, string> = {
    'pending': 'bg-amber-50 text-amber-700 border-amber-200',
    'confirmed': 'bg-blue-50 text-blue-700 border-blue-200',
    'approved': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'done': 'bg-gray-50 text-gray-700 border-gray-200',
    'cancelled': 'bg-red-50 text-red-700 border-red-200',
    'canceled': 'bg-red-50 text-red-700 border-red-200',
  };
  return statusClasses[status] || 'bg-gray-50 text-gray-700 border-gray-200';
};

const getStatusText = (status: string): string => {
  const statusTexts: Record<string, string> = {
    'pending': 'Đang chờ',
    'confirmed': 'Đã xác nhận',
    'approved': 'Đã duyệt',
    'done': 'Hoàn thành',
    'cancelled': 'Đã hủy',
    'canceled': 'Đã hủy',
  };
  return statusTexts[status] || status;
};

const getStatusIcon = (status: string): JSX.Element => {
  const iconClasses = "w-4 h-4";
  switch (status) {
    case 'pending':
      return (
        <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'confirmed':
      return (
        <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'approved':
      return (
        <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'done':
      return (
        <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'cancelled':
    case 'canceled':
      return (
        <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    default:
      return (
        <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
  }
};

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ order, isOpen, onClose }) => {
  const [page, setPage] = useState(1);
  const perPage = 10;
  
  const [{ data, fetching, error }] = useQuery({
    query: ORDER_ITEMS_QUERY,
    variables: { orderId: order.id, first: perPage, page },
    pause: !isOpen,
  });

  // Reset page to 1 when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setPage(1);
    }
  }, [isOpen, order.id]);

  const orderItemsData: OrderItemsResponse | undefined = data?.orderItems;
  const orderItems = orderItemsData?.data || [];
  const pagination = orderItemsData?.pagination;

  const handlePrevious = () => {
    if (pagination && pagination.current_page > 1) {
      setPage(pagination.current_page - 1);
    }
  };

  const handleNext = () => {
    if (pagination && pagination.has_more_pages) {
      setPage(pagination.current_page + 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity backdrop-blur-sm" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
          {/* Header */}
          <div className="px-6 py-5" style={{ background: 'linear-gradient(to right, rgb(67, 128, 58), rgb(52, 110, 45))' }}>
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-white">
                  Chi tiết đơn hàng
                </h3>
                <p className="text-green-100 text-sm mt-1">#{order.track_code}</p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-green-100 focus:outline-none transition-colors duration-200 p-1 rounded-lg hover:bg-green-700"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="bg-white px-6 py-6">
            {/* Order Summary */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 mb-6 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Tổng tiền</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {Number(order.total).toLocaleString('vi-VN')} đ
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Trạng thái</p>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${getStatusBadgeClass(order.status)}`}>
                    {getStatusIcon(order.status)}
                    {getStatusText(order.status)}
                  </span>
                </div>
                {order.branch && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Chi nhánh</p>
                    <p className="text-sm font-semibold text-gray-900">{order.branch.name}</p>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800 font-medium">Có lỗi xảy ra: {error.message}</p>
                  </div>
                </div>
              </div>
            )}

            {fetching && (
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {[1, 2, 3, 4, 5].map((index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200 animate-pulse">
                    <div className="flex-shrink-0">
                      <div className="w-24 h-24 bg-gray-200 rounded-lg"></div>
                    </div>
                    <div className="flex-1 min-w-0 space-y-3">
                      <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right space-y-2">
                      <div className="h-6 bg-gray-200 rounded w-28 ml-auto"></div>
                      <div className="h-4 bg-gray-200 rounded w-24 ml-auto"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!fetching && !error && orderItems.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <p className="text-gray-500 font-medium">Không có sản phẩm nào trong đơn hàng này</p>
              </div>
            )}

            {!fetching && !error && orderItems.length > 0 && (
              <>
                {pagination && (
                  <div className="mb-4 px-2">
                    <p className="text-sm text-gray-600">
                      Hiển thị <span className="font-semibold text-gray-900">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> - <span className="font-semibold text-gray-900">{Math.min(pagination.current_page * pagination.per_page, pagination.total)}</span> của <span className="font-semibold text-gray-900">{pagination.total}</span> sản phẩm
                    </p>
                  </div>
                )}
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                  {orderItems.map((item: OrderItem) => {
                    const quantity = item.quantity || item.qty || 0;
                    const subtotal = item.subtotal || (item.price * quantity);
                    const imageUrl = item.product?.main_image_url;
                    
                    return (
                      <div key={item.id} className="flex items-start gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-all duration-200" style={{ '--hover-border-color': 'rgb(67, 128, 58)' } as React.CSSProperties & { '--hover-border-color': string }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgb(67, 128, 58)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}>
                        {/* Product Image */}
                        <div className="flex-shrink-0">
                          {imageUrl ? (
                            <img 
                              src={imageUrl} 
                              alt={item.product?.name || 'Sản phẩm'}
                              className="w-24 h-24 object-cover rounded-lg border border-gray-200 shadow-sm"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = 'https://via.placeholder.com/96x96?text=No+Image';
                              }}
                            />
                          ) : (
                            <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg border border-gray-200 flex items-center justify-center shadow-sm">
                              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        
                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <h5 className="text-base font-bold text-gray-900 mb-2">
                            {item.product?.name || 'Sản phẩm không xác định'}
                          </h5>
                          
                          {item.productVariant && (
                            <div className="mb-2 inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border" style={{ backgroundColor: 'rgba(67, 128, 58, 0.1)', color: 'rgb(67, 128, 58)', borderColor: 'rgba(67, 128, 58, 0.2)' }}>
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                              </svg>
                              {item.productVariant.name}
                            </div>
                          )}
                          
                          {item.productVariant?.sku && (
                            <p className="text-xs text-gray-500 mb-2">
                              SKU: <span className="font-medium">{item.productVariant.sku}</span>
                            </p>
                          )}
                          
                          <div className="flex flex-wrap items-center gap-4 mt-3">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded-md text-xs font-medium">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                SL: {quantity}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600">
                              Đơn giá: <span className="font-semibold text-gray-900">
                                {Number(item.price).toLocaleString('vi-VN')} đ
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Subtotal */}
                        <div className="flex-shrink-0 text-right">
                          <p className="text-lg font-bold text-gray-900 mb-1">
                            {Number(subtotal).toLocaleString('vi-VN')} đ
                          </p>
                          <p className="text-xs text-gray-500">
                            = {quantity} × {Number(item.price).toLocaleString('vi-VN')} đ
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {pagination && pagination.last_page > 1 && (
                  <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                    <button
                      onClick={handlePrevious}
                      disabled={pagination.current_page === 1}
                      className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        pagination.current_page === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 shadow-sm hover:shadow'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Trước
                    </button>
                    <span className="text-sm text-gray-700 font-medium">
                      Trang {pagination.current_page} / {pagination.last_page}
                    </span>
                    <button
                      onClick={handleNext}
                      disabled={!pagination.has_more_pages}
                      className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        !pagination.has_more_pages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 shadow-sm hover:shadow'
                      }`}
                    >
                      Sau
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center items-center gap-2 rounded-lg border border-transparent shadow-sm px-5 py-2.5 bg-gray-600 text-base font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Đóng
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const UserOrders = () => {
  const currentUserInfo = useAppSelector(getAuthUserInfo);
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [trackCodeFilter, setTrackCodeFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  
  const [{ data, fetching, error }] = useQuery({ 
    query: ALL_ORDERS_QUERY,
    variables: { 
      first: perPage, 
      page,
      track_code: trackCodeFilter || undefined,
      date_from: dateFromFilter || undefined,
      date_to: dateToFilter || undefined,
    }
  });
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const ordersData: OrdersResponse | undefined = data?.allOrders;
  const orders = ordersData?.data || [];
  const pagination = ordersData?.pagination;

  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePrevious = () => {
    if (pagination && pagination.current_page > 1) {
      setPage(pagination.current_page - 1);
    }
  };

  const handleNext = () => {
    if (pagination && pagination.has_more_pages) {
      setPage(pagination.current_page + 1);
    }
  };

  const handleFilter = () => {
    setPage(1); // Reset to first page when filtering
  };

  const handleClearFilter = () => {
    setTrackCodeFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setPage(1);
  };

  return (
      <AvoRedApp>
        <div className="min-h-screen bg-gray-50">
          {/* Breadcrumb */}
          <nav className="bg-white shadow-sm border-b border-gray-200">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 items-center space-x-2 text-sm">
                <Link 
                  to="/" 
                  className="text-gray-500 hover:text-green-600 transition-colors duration-200 flex items-center"
                >
                  <svg 
                    className="w-4 h-4 mr-1" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" 
                    />
                  </svg>
                  Trang chủ
                </Link>
                <span className="text-gray-400">/</span>
                <Link 
                  to="/profile"
                  className="text-gray-500 hover:text-green-600 transition-colors duration-200"
                >
                  Tài khoản
                </Link>
                <span className="text-gray-400">/</span>
                <span className="text-gray-900 font-medium">Đơn hàng của tôi</span>
              </div>
            </div>
          </nav>

          {/* Header */}
          <div className="bg-white shadow">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                Đơn hàng của tôi
            </h1>
              <p className="mt-2 text-gray-600">
                Quản lý và theo dõi đơn hàng của bạn
              </p>
            </div>
          </div>

        <main className="min-h-screen">
          <div className="mx-auto max-w-7xl py-8 sm:px-6 lg:px-8">
            <div className="px-4 sm:px-0">
                <div className='flex flex-col lg:flex-row w-full gap-6'>
                  <div className='w-full lg:w-64'>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <UserSidebar user={currentUserInfo} />
                    </div>
                  </div>
                  <div className='flex-1'>
                    {/* Filter Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                      <div className="flex flex-col md:flex-row gap-4 items-end">
                        <div className="flex-1 min-w-0">
                          <label htmlFor="track_code" className="block text-sm font-medium text-gray-700 mb-2">
                            Mã đơn hàng
                          </label>
                          <input
                            type="text"
                            id="track_code"
                            value={trackCodeFilter}
                            onChange={(e) => setTrackCodeFilter(e.target.value)}
                            placeholder="Nhập mã đơn hàng..."
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <label htmlFor="date_from" className="block text-sm font-medium text-gray-700 mb-2">
                            Từ ngày
                          </label>
                          <input
                            type="date"
                            id="date_from"
                            value={dateFromFilter}
                            onChange={(e) => setDateFromFilter(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <label htmlFor="date_to" className="block text-sm font-medium text-gray-700 mb-2">
                            Đến ngày
                          </label>
                          <input
                            type="date"
                            id="date_to"
                            value={dateToFilter}
                            onChange={(e) => setDateToFilter(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleFilter}
                            className="px-6 py-2 rounded-lg text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow"
                            style={{ 
                              background: 'linear-gradient(to right, rgb(67, 128, 58), rgb(52, 110, 45))',
                            }}
                            onMouseEnter={(e) => { 
                              e.currentTarget.style.background = 'linear-gradient(to right, rgb(52, 110, 45), rgb(43, 95, 39))';
                            }}
                            onMouseLeave={(e) => { 
                              e.currentTarget.style.background = 'linear-gradient(to right, rgb(67, 128, 58), rgb(52, 110, 45))';
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                              </svg>
                              Lọc
                            </div>
                          </button>
                          {(trackCodeFilter || dateFromFilter || dateToFilter) && (
                            <button
                              onClick={handleClearFilter}
                              className="px-6 py-2 rounded-lg text-gray-700 bg-white border border-gray-300 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 shadow-sm hover:shadow"
                            >
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Xóa
                              </div>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {error && (
                      <div className="bg-red-50 border-l-4 border-red-400 rounded-md p-4 mb-6 shadow-sm">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-red-800 font-medium">Có lỗi xảy ra khi tải đơn hàng: {error.message}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {fetching && (
                      <div className="space-y-4">
                        {[1, 2, 3, 4].map((index) => (
                          <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden animate-pulse">
                            <div className="p-6">
                              <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                                {/* Left side skeleton */}
                                <div className="flex-1 space-y-3">
                                  <div className="h-6 bg-gray-200 rounded w-48"></div>
                                  <div className="h-4 bg-gray-200 rounded w-56"></div>
                                  <div className="h-4 bg-gray-200 rounded w-40"></div>
                                </div>
                                {/* Right side skeleton */}
                                <div className="text-right space-y-3">
                                  <div className="h-6 bg-gray-200 rounded-full w-28 ml-auto"></div>
                                  <div className="h-8 bg-gray-200 rounded w-36 ml-auto"></div>
                                </div>
                              </div>
                              {/* Button skeleton */}
                              <div className="mt-6 border-t border-gray-200 pt-4">
                                <div className="h-10 bg-gray-200 rounded-lg w-48"></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {!fetching && !error && orders.length === 0 && (
                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                        <div className="text-center">
                          <svg className="mx-auto h-24 w-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <h3 className="mt-4 text-lg font-medium text-gray-900">Chưa có đơn hàng nào</h3>
                          <p className="mt-2 text-sm text-gray-500">Khi bạn đặt hàng, chúng sẽ xuất hiện ở đây.</p>
                        </div>
                      </div>
                    )}

                    {!fetching && !error && orders.length > 0 && (
                      <>
                        {pagination && (
                          <div className="mb-6 px-2">
                            <p className="text-sm text-gray-600">
                              Hiển thị <span className="font-semibold text-gray-900">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> - <span className="font-semibold text-gray-900">{Math.min(pagination.current_page * pagination.per_page, pagination.total)}</span> của <span className="font-semibold text-gray-900">{pagination.total}</span> đơn hàng
                            </p>
                          </div>
                        )}
                        <div className="space-y-4">
                          {orders.map((order: Order) => (
                            <div key={order.id} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
                              <div className="p-6">
                                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-6">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      <div className="flex-shrink-0">
                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(to bottom right, rgb(67, 128, 58), rgb(52, 110, 45))' }}>
                                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                          </svg>
                                        </div>
                                      </div>
                                      <div>
                                        <h3 className="text-lg font-bold text-gray-900">
                                          Đơn hàng #{order.track_code}
                                        </h3>
                                        <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                          </svg>
                                          {formatDate(order.created_at)}
                                        </p>
                                      </div>
                                    </div>
                                    {order.branch && (
                                      <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span className="font-medium">{order.branch.name}</span>
                                        {order.branch.address && (
                                          <span className="text-gray-500">• {order.branch.address}</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex flex-col items-end gap-3">
                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${getStatusBadgeClass(order.status)}`}>
                                      {getStatusIcon(order.status)}
                                      {getStatusText(order.status)}
                                    </span>
                                    <div className="text-right">
                                      <p className="text-xs text-gray-500 mb-1">Tổng tiền</p>
                                      <p className="text-2xl font-bold text-gray-900">
                                        {Number(order.total).toLocaleString('vi-VN')} đ
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                <div className="pt-4 border-t border-gray-200">
                                  <button
                                    onClick={() => handleViewDetails(order)}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow"
                                    style={{ 
                                      background: 'linear-gradient(to right, rgb(67, 128, 58), rgb(52, 110, 45))',
                                    }}
                                    onMouseEnter={(e) => { 
                                      e.currentTarget.style.background = 'linear-gradient(to right, rgb(52, 110, 45), rgb(43, 95, 39))';
                                    }}
                                    onMouseLeave={(e) => { 
                                      e.currentTarget.style.background = 'linear-gradient(to right, rgb(67, 128, 58), rgb(52, 110, 45))';
                                    }}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    Xem chi tiết sản phẩm
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Pagination */}
                        {pagination && pagination.last_page > 1 && (
                          <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                              <div className="text-sm text-gray-600">
                                Trang <span className="font-semibold text-gray-900">{pagination.current_page}</span> / <span className="font-semibold text-gray-900">{pagination.last_page}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={handlePrevious}
                                  disabled={pagination.current_page === 1}
                                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                                    pagination.current_page === 1
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 shadow-sm hover:shadow'
                                  }`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                  </svg>
                                  Trước
                                </button>
                                <button
                                  onClick={handleNext}
                                  disabled={!pagination.has_more_pages}
                                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                                    !pagination.has_more_pages
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 shadow-sm hover:shadow'
                                  }`}
                                >
                                  Sau
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
            </div>
          </div>
        </main>
        
        {selectedOrder && (
          <OrderDetailsModal
            order={selectedOrder}
            isOpen={isModalOpen}
            onClose={handleCloseModal}
          />
        )}
        </div>
      </AvoRedApp>
    )
}
